import '@js-joda/timezone/dist/js-joda-timezone-10-year-range.js';

import i18n, {
  type FallbackLngObjList,
  type Namespace,
  type ParseKeys,
} from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { SET_PHONE_LANGUAGE } from '~/reducers/env/types';
import * as log from '~/utilities/log';
import mediator from '~/utilities/mediator';
import { updateChosenLang } from '~/utilities/storage';
import { store } from '~/utilities/store';

const enTranslations = await import('~/locales/en/translations.json');

/** @public */
export const namespace = 'translations';
let initialized = false;
let firstLangSet = false;
let currentLang = '';
/**
 * Construct an ordered array of unique languages to support based on the passed in language.
 * The order is constructed with the original language, followed by its corresponding fallback languages
 * ending with our default languages.
 *
 * This ordered structure is handy for both i18n and date-lib
 */
const _getSupportedLangs = (lang: string): string[] => {
  // This should be the configurable list of supported languages
  const langFallBack = {
    default: ['en-US'],
    en: ['en-US'],
    'en-US': ['en-US'],
    // en: ['en-US'],
    // 'es-ES': [],
    // es: ['es-ES'],
    // 'es-US': ['es-ES'],
  } as {
    default: string[];
    [key: string]: string[];
  };

  let fallbacks = langFallBack[lang] ?? [];
  const langs = lang ? [lang] : [];

  // Add 2 char fallbacks after 5 char fallbacks
  fallbacks = fallbacks.concat(langFallBack[lang.slice(0, 2)] ?? []);

  return [...new Set([...langs, ...fallbacks, ...langFallBack.default])];
};

/**
 * Takes a languages array and attempts to find a translation file that matches each language.
 * We build an array that contains all successfully loaded translations in the same relative order.
 */
const _requireTranslation = async (langs: string[]) => {
  const locales = [];

  for (const lang of langs) {
    try {
      /* @vite-ignore */
      const locale = (await import(`../locales/${lang}/translations.json`)) as
        | string
        | undefined;

      if (!locale) {
        throw new Error(`Locale ${lang} not found. Using fallback.`);
      }

      locales.push({
        locale,
        lang,
      });
    } catch (e) {
      log.debug(JSON.stringify(e));
      // if none of locales in array was found he return en-US as fallback
      locales.push({
        locale: enTranslations,
        lang,
      });
    }
  }

  return locales;
};

// Set the HTML Document title based on language
const _changeDocumentTitle = () => {
  window.document.title = i18n.t('Global.microcopy.common.title');
};

// Set the HTML Document direction attribute based on language
const _changeHtmlDirection = (lang: string) => {
  let dir = 'ltr';

  switch (lang.slice(0, 2)) {
    case 'ar':
    case 'he':
      dir = 'rtl';
  }

  document.documentElement.dir = dir;
  log.debug(`html: setting document direction to ${dir}`);
};

// Set i18n language and set date library global locale support to be similar
const _setLanguage = async (lang: string) => {
  await i18n.changeLanguage(lang, (err) => {
    if (err) {
      log.error(err);
    } else {
      updateChosenLang(lang);
      _changeDocumentTitle();
      _changeHtmlDirection(lang);

      currentLang = lang;

      store.dispatch({ type: SET_PHONE_LANGUAGE, phoneLanguage: lang });

      // Make sure it isn't the crowdin inContext language
      if (lang !== 'la-LA') {
        mediator.publish('langChange:success', { lang });
      }

      // Let the app know i19n is ready to be used
      if (!firstLangSet) {
        firstLangSet = true;
        mediator.publish('app:ready', { i18n: true });
      }
    }
  });
};

// Handle importing all valid languages that the passed in language supports and set the app language
const _handleChange = async (lang: string) => {
  if (!initialized) {
    return;
  }

  if (lang === currentLang) {
    return;
  }

  const setLocales = await _requireTranslation(_getSupportedLangs(lang));

  if (setLocales.length === 0) {
    log.panic('Unable to find any valid translation resource');
  }

  // We load all possible locales into i18n that we can, so i18n can handle it's internal missing key fallbacks
  for (const l of setLocales) {
    if (!i18n.hasResourceBundle(l.lang, namespace)) {
      i18n.addResourceBundle(l.lang, namespace, l.locale);
    }
  }

  // We use the first available locale as the language to set
  await _setLanguage(setLocales[0]?.lang ?? 'en-US');
};

/**
 * Callback method to trigger after i18n has initialized.
 */
const initComplete = async () => {
  initialized = true;
  await _handleChange('en-US');
};

// Want to wait until the store is ready to set up the subscription and initialize i18n so we can use config values.
const init = async () => {
  const {
    langFallBack = {
      default: ['en-US'],
      en: ['en-US'],
      'es-ES': [],
      es: ['es-ES'],
      'es-US': ['es-ES'],
    },
    langWhiteList = ['en', 'en-US', 'ar', 'es', 'es-ES', 'es-US'],
  } = {};

  // We append default to the end of all fallbacks for i18n key resolution
  const i18nLangFallback = Object.entries(
    langFallBack,
  ).reduce<FallbackLngObjList>((pv, [k, v]) => {
    const langs = [...v];

    langFallBack.default.forEach((l: string) => {
      if (!langs.includes(l)) {
        langs.push(l);
      }
    });

    pv[k] = langs;

    return pv;
  }, {});

  await i18n.use(LanguageDetector).init(
    {
      cleanCode: true,
      debug: import.meta.env.DEV,
      defaultNS: namespace,
      detection: {
        caches: [],
        order: ['querystring', 'navigator'],
      },
      fallbackLng: i18nLangFallback,
      interpolation: {
        escapeValue: false,
      },
      nonExplicitSupportedLngs: true,
      ns: [namespace],
      supportedLngs: langWhiteList.length > 0 ? langWhiteList : false,
    },
    () => void initComplete(),
  );
};

mediator.once('init:i18n', init);

// Want to wait until the store is ready to set up the subscription and initialize i18n so we can use config values.
mediator.once('store:created', async () => {
  await init();
});

mediator.subscribe('trans:change', _handleChange);

export default i18n;

export type TFuncKey = ParseKeys<Namespace>;
